import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";

export default function DepositHistory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);

  // Filters
  const [bookingId, setBookingId] = useState("");
  const [paymentIntentId, setPaymentIntentId] = useState("");
  const [action, setAction] = useState("all"); // all | capture | release
  const [status, setStatus] = useState("all"); // all | requires_capture | succeeded | canceled | processing | unknown

  const pageSize = 25;

  const makeQuery = async (cursor) => {
    const col = collection(db, "depositLogs");
    let q = query(col, orderBy("timestamp", "desc"), limit(pageSize));

    if (bookingId.trim()) {
      q = query(q, where("bookingId", "==", bookingId.trim()));
    }
    if (paymentIntentId.trim()) {
      q = query(q, where("paymentIntentId", "==", paymentIntentId.trim()));
    }
    if (action !== "all") {
      q = query(q, where("action", "==", action));
    }
    if (status !== "all") {
      q = query(q, where("status", "==", status));
    }
    if (cursor) {
      q = query(q, startAfter(cursor));
    }
    const snap = await getDocs(q);
    return snap;
  };

  const load = async (cursor = null, append = false) => {
    setLoading(true);
    try {
      const snap = await makeQuery(cursor);
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data(), _doc: d }));
      setNextCursor(snap.docs.length ? snap.docs[snap.docs.length - 1] : null);
      setItems((old) => (append ? [...old, ...rows] : rows));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // initial load
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = async () => {
    setNextCursor(null);
    await load();
  };

  const toDollars = (maybeCents) => {
    if (maybeCents == null) return "-";
    // logs store cents (number). If someone logged dollars, try to detect.
    const n = Number(maybeCents);
    if (!Number.isFinite(n)) return "-";
    // If it's < $1000 and has decimals already, assume dollars
    if (n < 1000 && String(n).includes(".")) return `$${n.toFixed(2)}`;
    return `$${(n / 100).toFixed(2)}`;
  };

  const csv = useMemo(() => {
    const header = [
      "timeISO",
      "bookingId",
      "paymentIntentId",
      "action",
      "status",
      "amountCaptured(cents)",
      "note",
      "adminEmail",
      "adminUid",
    ].join(",");
    const lines = items.map((r) =>
      [
        r.isoTime ||
          (r.timestamp instanceof Timestamp
            ? r.timestamp.toDate().toISOString()
            : ""),
        r.bookingId || "",
        r.paymentIntentId || "",
        r.action || "",
        r.status || "",
        r.amountCaptured ?? "",
        (r.note || "").replace(/,/g, " "),
        r?.triggeredBy?.email || "",
        r?.triggeredBy?.uid || "",
      ].join(",")
    );
    return [header, ...lines].join("\n");
  }, [items]);

  const downloadCSV = () => {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `deposit-history-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>🧾 Deposit History</h2>

      <div style={styles.filters}>
        <input
          style={styles.input}
          placeholder="Booking ID"
          value={bookingId}
          onChange={(e) => setBookingId(e.target.value)}
        />
        <input
          style={styles.input}
          placeholder="PaymentIntent ID"
          value={paymentIntentId}
          onChange={(e) => setPaymentIntentId(e.target.value)}
        />
        <select
          style={styles.input}
          value={action}
          onChange={(e) => setAction(e.target.value)}
        >
          <option value="all">All actions</option>
          <option value="capture">Capture</option>
          <option value="release">Release</option>
        </select>
        <select
          style={styles.input}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="all">All statuses</option>
          <option value="requires_capture">requires_capture</option>
          <option value="succeeded">succeeded</option>
          <option value="canceled">canceled</option>
          <option value="processing">processing</option>
          <option value="unknown">unknown</option>
        </select>
        <button onClick={onSearch} style={styles.primaryBtn} disabled={loading}>
          {loading ? "Loading..." : "Search"}
        </button>
        <button onClick={downloadCSV} style={styles.secondaryBtn}>
          Export CSV
        </button>
      </div>

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th>When</th>
              <th>Action</th>
              <th>Amount</th>
              <th>Booking</th>
              <th>PaymentIntent</th>
              <th>Status</th>
              <th>Note</th>
              <th>By</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading ? (
              <tr>
                <td colSpan="8" style={{ textAlign: "center", padding: 16 }}>
                  No results
                </td>
              </tr>
            ) : (
              items.map((r) => (
                <tr key={r.id}>
                  <td>
                    {r.isoTime ||
                      (r.timestamp?.toDate
                        ? r.timestamp.toDate().toLocaleString()
                        : "-")}
                  </td>
                  <td style={{ textTransform: "capitalize" }}>{r.action}</td>
                  <td>{toDollars(r.amountCaptured)}</td>
                  <td title={r.bookingId || ""}>
                    {r.bookingId ? r.bookingId.slice(0, 8) + "…" : "-"}
                  </td>
                  <td title={r.paymentIntentId || ""}>
                    {r.paymentIntentId
                      ? r.paymentIntentId.slice(0, 10) + "…"
                      : "-"}
                  </td>
                  <td>{r.status || "-"}</td>
                  <td>{r.note || "-"}</td>
                  <td>{r?.triggeredBy?.email || "admin"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {nextCursor && (
        <button
          onClick={() => load(nextCursor, true)}
          style={{ ...styles.secondaryBtn, marginTop: 12 }}
          disabled={loading}
        >
          {loading ? "Loading…" : "Load more"}
        </button>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: "2rem",
    fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
    backgroundColor: "#f9fafb",
    minHeight: "100vh",
  },
  title: { fontSize: "1.5rem", fontWeight: 700, color: "#065f46", marginBottom: 12 },
  filters: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    alignItems: "center",
    marginBottom: 12,
  },
  input: {
    padding: "8px 10px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    minWidth: 180,
    background: "#fff",
  },
  primaryBtn: {
    backgroundColor: "#22c55e",
    color: "#fff",
    fontWeight: 700,
    padding: "8px 12px",
    borderRadius: 6,
    border: "none",
    cursor: "pointer",
  },
  secondaryBtn: {
    backgroundColor: "#e5e7eb",
    color: "#111827",
    fontWeight: 600,
    padding: "8px 12px",
    borderRadius: 6,
    border: "none",
    cursor: "pointer",
  },
  tableWrap: {
    overflowX: "auto",
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
  },
};
